/**
 * 하루치 이벤트 겹침 레이아웃 — 시간이 겹치는 이벤트(수업·상담 불문)를 클러스터로 묶어
 * 컬럼을 배정한다. 반환: Map(event -> { col, cols })
 *
 * 판정은 실제 시작·종료 시각만 본다. 짧은 일정을 최소 길이로 늘려 잡으면 10:50~10:55 와 11:00 처럼
 * 맞닿지도 않은 일정이 좌우로 쪼개진다. 최소 카드 높이 때문에 생기는 시각적 겹침은 분할 대상이 아니고,
 * 늦게 시작한 카드가 앞 카드를 덮는다(hover 하면 그 카드가 위로 올라온다).
 *
 * 주간 보기(PrivateLessonWeekView)와 일간 보기(PrivateLessonEventCard)가 같은 규칙을 쓴다.
 */
export const layoutDayEvents = dayEvents => {
  const items = dayEvents
    .map(ev => {
      const start = ev.startHour + ev.startMinute / 60;
      return { ev, start, end: Math.max(ev.endHour + ev.endMinute / 60, start), col: 0 };
    })
    .sort((a, b) => a.start - b.start || b.end - a.end);

  const layout = new Map();
  let cluster = [];
  let colEnds = []; // 클러스터 내 컬럼별 마지막 종료시각
  let clusterEnd = -1;

  const closeCluster = () => {
    cluster.forEach(item => layout.set(item.ev, { col: item.col, cols: colEnds.length }));
    cluster = [];
    colEnds = [];
    clusterEnd = -1;
  };

  items.forEach(item => {
    if (cluster.length > 0 && item.start >= clusterEnd) closeCluster();
    let col = colEnds.findIndex(end => end <= item.start);
    if (col === -1) {
      col = colEnds.length;
      colEnds.push(item.end);
    } else {
      colEnds[col] = item.end;
    }
    item.col = col;
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, item.end);
  });
  closeCluster();
  return layout;
};

/**
 * 겹쳐 가려진 카드도 첫 줄(시간)은 남기기 위한 세로 보정.
 *
 * 최소 카드 높이 때문에 짧은 일정은 실제 길이보다 크게 그려지고, 뒤 일정이 그 위를 덮는다. 이때
 * 두 일정의 시작 간격이 좁으면(10:50 → 11:00 처럼) 앞 카드에서 보이는 부분이 시간 줄보다 얇아진다.
 * 그래서 뒤 카드를 아래로 밀어 앞 카드의 상단 minExposePx 는 항상 남긴다.
 *
 * 밀어내기는 같은 컬럼(좌우로 분할되지 않은, 실제로는 안 겹치는 사이)에서만 한다. 실제 시간이 겹쳐
 * 좌우로 나뉜 카드끼리는 서로 가리지 않으므로 밀 이유가 없다.
 *
 * @returns Map(event -> 밀어낸 픽셀 수) — 0 이면 보정 없음
 */
export const layoutStackedOffsets = (dayEvents, hourHeight, minExposePx) => {
  const columns = layoutDayEvents(dayEvents);
  const byColumn = new Map();
  dayEvents.forEach(ev => {
    const col = columns.get(ev)?.col ?? 0;
    if (!byColumn.has(col)) byColumn.set(col, []);
    byColumn.get(col).push(ev);
  });

  const offsets = new Map();
  byColumn.forEach(events => {
    let prevTop = -Infinity;
    [...events]
      .sort((a, b) => a.startHour + a.startMinute / 60 - (b.startHour + b.startMinute / 60))
      .forEach(ev => {
        const rawTop = (ev.startHour + ev.startMinute / 60) * hourHeight;
        const top = Math.max(rawTop, prevTop + minExposePx);
        offsets.set(ev, top - rawTop);
        prevTop = top;
      });
  });
  return offsets;
};

export default layoutDayEvents;
